import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { type LngLatLike, type Map, type StyleSpecification } from 'maplibre-gl';
import {
  Ban,
  Coins,
  Compass,
  Crown,
  ExternalLink,
  Globe2,
  ImageOff,
  ListChecks,
  MapPin,
  Play,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trophy,
  User,
  Wallet,
  X
} from 'lucide-react';
import {
  ApiError,
  api,
  type AppConfig,
  type Player,
  type PlayerProfile,
  type PublicFeedEvent,
  type RoundState,
  type TapResponse,
  type TreasurePlace
} from './api';
import { LANGUAGES, languageMeta, readStoredLanguage, t, type Language, type TranslationKey } from './i18n';

const COUNTRIES = [
  'Kazakhstan',
  'Armenia',
  'Georgia',
  'Serbia',
  'Montenegro',
  'Argentina',
  'Brazil',
  'Mexico',
  'Israel',
  'United Arab Emirates',
  'Thailand',
  'Vietnam',
  'Other'
] as const;

type Country = typeof COUNTRIES[number];
type T = (key: TranslationKey) => string;
type MapLayer = 'map' | 'satellite';

function normalizeLng(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

function validTapPoint(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -85 && lat <= 85 && lng >= -180 && lng <= 180;
}

function eoxSatelliteStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {
      'eox-sentinel-2': {
        type: 'raster',
        tiles: [
          'https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2024_3857/default/g/{z}/{y}/{x}.jpg'
        ],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 14
      }
    },
    layers: [
      {
        id: 'eox-sentinel-2',
        type: 'raster',
        source: 'eox-sentinel-2',
        paint: {
          'raster-resampling': 'linear'
        }
      }
    ]
  };
}

function mapStyleForLayer(styleUrl: string, layer: MapLayer) {
  return layer === 'satellite' ? eoxSatelliteStyle() : styleUrl;
}

type RulesContent = {
  title: string;
  subtitle: string;
  backToGame: string;
  updated: string;
  sections: Array<{
    title: string;
    body?: string;
    bullets: string[];
  }>;
};

const RULES_CONTENT: Record<Language, RulesContent> = {
  en: {
    title: 'HotCold Game Rules',
    subtitle: 'A map treasure hunt where every tap gives an approximate clue to the nearest hidden treasure.',
    backToGame: 'Back to game',
    updated: 'MVP0 web version rules',
    sections: [
      {
        title: '1. Goal of the game',
        body: 'HotCold is a world-map treasure hunt. Your goal is to find hidden virtual treasures faster and more efficiently than other players.',
        bullets: [
          'Each round has 10 hidden treasures placed at recognizable real-world points of interest.',
          'A tap on the map spends points and returns an approximate distance and direction to the nearest unfound treasure.',
          'The search narrows naturally from world level to country, city, street area and finally the exact place.'
        ]
      },
      {
        title: '2. Points and value',
        bullets: [
          'The MVP0 web version uses entertainment points only.',
          'Points can be spent on search taps and can be won as virtual rewards.',
          'Points cannot be withdrawn, transferred, sold or exchanged for cash, crypto, USDT, prizes or anything with monetary value.',
          'The current balance-add screen is for testing only and does not process real payments.'
        ]
      },
      {
        title: '3. Rounds',
        bullets: [
          'A round lasts up to 2 hours.',
          'A round ends earlier if all 10 treasures are found.',
          'The next round starts 1 hour after the previous round ends.',
          'Round timing uses server time. If a request reaches the server after the round ends, it is rejected and points are not spent.',
          'Players may join an active round at any time.'
        ]
      },
      {
        title: '4. Treasure rewards',
        bullets: [
          'Every round has exactly 10 treasures: 1 very large, 2 large, 5 medium and 2 small.',
          'The very large treasure receives 34% of the round bank.',
          'Each large treasure receives 16% of the round bank.',
          'Each medium treasure receives 6% of the round bank.',
          'Each small treasure receives 2% of the round bank.',
          'Reward amounts are visible before and during the round.'
        ]
      },
      {
        title: '5. Taps and clues',
        bullets: [
          'Each paid tap costs the fixed tap price shown in the game panel.',
          'A tap returns a clue to the nearest still-unfound treasure at that exact server moment.',
          'The clue includes deliberately approximate distance and broad direction, not exact coordinates or an exact bearing.',
          'Below 100 meters the direction arrow is hidden. Distance is still approximate and never exact.',
          'If a treasure has already been found by someone else, your next tap targets the nearest remaining treasure.'
        ]
      },
      {
        title: '6. Country and city messages',
        bullets: [
          'When your tap lands in the country of the nearest treasure, the game shows a country-hit message.',
          'When your tap gets close enough to the treasure city area, the game shows a city-hit message.',
          'These messages are hints only. They do not reveal the exact point.'
        ]
      },
      {
        title: '7. Claiming a treasure',
        bullets: [
          'A treasure is found automatically when your tap is close enough and the map is zoomed in far enough.',
          'In MVP0, you need to be within 25 meters of the treasure.',
          'The server also checks that the map is zoomed in far enough.',
          'The first valid finding request that reaches the server wins that treasure.',
          'When you find a treasure, you see a window with the place name, category, city, country, short description and photo when available.'
        ]
      },
      {
        title: '8. Rewards after the round',
        bullets: [
          'Rewards for found treasures are held until the round ends.',
          'After the round ends, those rewards are credited to the player balance.',
          'Unfound rewards roll over into the next round bank.',
          'Your found treasure cards are saved in your profile.'
        ]
      },
      {
        title: '9. Next round bank',
        bullets: [
          'Treasure reward amounts are fixed when the round starts.',
          'The bank shown during the round is the remaining value of still-unfound treasures and decreases when treasures are found.',
          'Points spent during the current round do not change current rewards.',
          '10% of each tap is kept as the platform fee and removed from the player reward pool.',
          'The estimated next round bank includes 90% of each tap plus the rewards of treasures that are still unfound right now.'
        ]
      },
      {
        title: '10. Public information',
        bullets: [
          'Your public game name is visible in the leaderboard, feed and public profile.',
          'Do not use a real name unless you want it to be visible.',
          'Other players can open your public profile from the leaderboard or feed.',
          'Public profile shows games count, treasures found, total reward points and treasure cards from finalized rounds.',
          'Your active-round treasure cards are visible only to you until the round ends.'
        ]
      },
      {
        title: '11. Fair play and abuse',
        bullets: [
          'Bots, scripts, direct technical misuse and coordinate manipulation are not allowed.',
          'Multi-account behavior intended to distort leaderboard, rewards or testing metrics may be blocked.',
          'Bug exploits, scraping hidden treasure locations and denial-of-service behavior are prohibited.',
          'The operator may block accounts and remove access for clear abuse.',
          'Technical signals such as IP address, session, browser and device data may be used for internal abuse review.'
        ]
      },
      {
        title: '12. Demo status',
        bullets: [
          'This is the MVP0 web version, a playable product and technical validation build.',
          'There is no Telegram integration, Telegram Stars, real payments, withdrawals, crypto or monetary prize flow in this version.',
          'Rules may be refined as the product moves from the MVP0 web version to Telegram MVP1.'
        ]
      }
    ]
  },
  ru: {
    title: 'Правила игры HotCold',
    subtitle: 'Охота за сокровищами на карте мира: каждое нажатие дает примерную подсказку к ближайшему спрятанному сокровищу.',
    backToGame: 'Вернуться в игру',
    updated: 'Правила веб-версии MVP0',
    sections: [
      {
        title: '1. Цель игры',
        body: 'HotCold - это охота за виртуальными сокровищами на карте мира. Цель игрока - найти спрятанные сокровища быстрее и эффективнее других участников.',
        bullets: [
          'В каждом раунде спрятано 10 сокровищ в узнаваемых реальных местах.',
          'Нажатие по карте списывает очки и дает примерное расстояние и направление до ближайшего ненайденного сокровища.',
          'Поиск постепенно сужается: мир, страна, город, район улицы, точное место.'
        ]
      },
      {
        title: '2. Очки и ценность',
        bullets: [
          'В веб-версии MVP0 используются только развлекательные очки.',
          'Очки можно тратить на поисковые нажатия и выигрывать как виртуальные награды.',
          'Очки нельзя вывести, передать, продать или обменять на деньги, криптовалюту, USDT, призы или любой денежный эквивалент.',
          'Текущее пополнение баланса - тестовая форма. Реальная оплата не проводится.'
        ]
      },
      {
        title: '3. Раунды',
        bullets: [
          'Раунд длится максимум 2 часа.',
          'Раунд заканчивается раньше, если найдены все 10 сокровищ.',
          'Следующий раунд начинается через 1 час после окончания предыдущего.',
          'Время считается по серверу. Если запрос приходит на сервер после окончания раунда, он отклоняется и очки не списываются.',
          'Игрок может подключиться к активному раунду в любой момент.'
        ]
      },
      {
        title: '4. Награды сокровищ',
        bullets: [
          'В каждом раунде ровно 10 сокровищ: 1 очень большое, 2 больших, 5 средних и 2 маленьких.',
          'Очень большое сокровище получает 34% банка раунда.',
          'Каждое большое сокровище получает 16% банка.',
          'Каждое среднее сокровище получает 6% банка.',
          'Каждое маленькое сокровище получает 2% банка.',
          'Размеры наград видны до и во время раунда.'
        ]
      },
      {
        title: '5. Нажатия и подсказки',
        bullets: [
          'Каждое платное нажатие стоит фиксированную цену, показанную в игровой панели.',
          'Нажатие дает подсказку к ближайшему еще не найденному сокровищу на момент обработки сервером.',
          'Подсказка содержит намеренно примерное расстояние и широкое направление, а не точные координаты или точный азимут.',
          'Ближе 100 метров стрелка направления скрывается. Расстояние остается примерным и никогда не становится точным.',
          'Если сокровище уже найдено другим игроком, следующее нажатие будет ориентироваться на ближайшее оставшееся сокровище.'
        ]
      },
      {
        title: '6. Сообщения о стране и городе',
        bullets: [
          'Если нажатие попало в страну ближайшего сокровища, игра показывает поздравление о правильной стране.',
          'Если нажатие достаточно близко к городскому району сокровища, игра показывает поздравление о правильном городе.',
          'Эти сообщения являются подсказками и не раскрывают точную точку.'
        ]
      },
      {
        title: '7. Как забрать сокровище',
        bullets: [
          'Сокровище забирается автоматически, если нажатие достаточно близко и карта достаточно приближена.',
          'В MVP0 радиус находки составляет 25 метров.',
          'Минимальный масштаб карты проверяется сервером.',
          'Побеждает первая корректная находка, дошедшая до сервера.',
          'После находки игрок видит всплывающее окно с названием места, категорией, городом, страной, кратким описанием и фото, если оно доступно.'
        ]
      },
      {
        title: '8. Начисление наград',
        bullets: [
          'Награды за найденные сокровища ожидают окончания раунда.',
          'После завершения раунда награды начисляются на баланс игрока.',
          'Ненайденные награды переносятся в банк следующего раунда.',
          'Карточки найденных сокровищ сохраняются в профиле игрока.'
        ]
      },
      {
        title: '9. Банк следующего раунда',
        bullets: [
          'Размеры наград сокровищ фиксируются в момент старта раунда.',
          'Банк, который виден во время раунда, показывает сумму еще ненайденных сокровищ и уменьшается после каждой находки.',
          'Очки, потраченные в текущем раунде, не меняют текущие награды.',
          '10% каждого нажатия остается как комиссия платформы и выводится из призового оборота.',
          'Прогноз банка следующего раунда включает 90% каждого нажатия плюс награды сокровищ, которые пока не найдены.'
        ]
      },
      {
        title: '10. Публичная информация',
        bullets: [
          'Публичное игровое имя видно в таблице лидеров, ленте и публичном профиле.',
          'Не используйте настоящее имя, если не хотите показывать его другим игрокам.',
          'Другие игроки могут открыть публичный профиль из таблицы лидеров или ленты.',
          'Публичный профиль показывает количество игр, найденные сокровища, общую сумму наград и карточки из завершенных раундов.',
          'Карточки активного раунда видны только владельцу до окончания раунда.'
        ]
      },
      {
        title: '11. Честная игра и нарушения',
        bullets: [
          'Автоматизированные аккаунты, программы для автоматической игры, прямое техническое злоупотребление и подмена координат запрещены.',
          'Использование нескольких аккаунтов для искажения таблицы лидеров, наград или тестовых метрик может привести к блокировке.',
          'Запрещены эксплуатация ошибок, попытки вычислить скрытые точки и атаки на доступность сервиса.',
          'Оператор может блокировать аккаунты при явных нарушениях.',
          'IP-адрес, сессии, браузер и данные устройства могут использоваться для внутренней проверки нарушений.'
        ]
      },
      {
        title: '12. Статус тестовой версии',
        bullets: [
          'Это веб-версия MVP0 - рабочая игровая и техническая версия для проверки продукта.',
          'В этой версии нет Telegram, Telegram Stars, реальных платежей, вывода средств, криптовалюты или денежных призов.',
          'Правила могут уточняться при переходе от веб-версии MVP0 к Telegram MVP1.'
        ]
      }
    ]
  },
  he: {
    title: 'כללי המשחק HotCold',
    subtitle: 'ציד אוצרות על מפת העולם: כל לחיצה נותנת רמז משוער לאוצר הקרוב ביותר שעדיין לא נמצא.',
    backToGame: 'חזרה למשחק',
    updated: 'כללי גרסת הדפדפן MVP0',
    sections: [
      {
        title: '1. מטרת המשחק',
        body: 'HotCold הוא ציד אוצרות וירטואליים על מפת העולם. המטרה היא למצוא אוצרות מוסתרים מהר וביעילות.',
        bullets: [
          'בכל סבב יש 10 אוצרות שמוסתרים בנקודות עניין אמיתיות ומוכרות.',
          'לחיצה על המפה עולה נקודות ונותנת מרחק וכיוון משוערים לאוצר הקרוב ביותר שלא נמצא.',
          'החיפוש מצטמצם בהדרגה: עולם, מדינה, עיר, אזור רחוב ולבסוף מקום מדויק.'
        ]
      },
      {
        title: '2. נקודות וערך',
        bullets: [
          'בגרסת הדפדפן MVP0 משתמשים בנקודות בידור בלבד.',
          'אפשר להוציא נקודות על לחיצות חיפוש ולזכות בנקודות כפרסים וירטואליים.',
          'אי אפשר למשוך, להעביר, למכור או להמיר נקודות לכסף, מטבעות דיגיטליים, USDT, פרסים או ערך כספי כלשהו.',
          'טעינת היתרה הנוכחית היא לצורכי בדיקה בלבד ואינה מבצעת תשלום אמיתי.'
        ]
      },
      {
        title: '3. סבבים',
        bullets: [
          'סבב נמשך עד שעתיים.',
          'הסבב מסתיים מוקדם אם כל 10 האוצרות נמצאו.',
          'הסבב הבא מתחיל שעה אחרי סיום הסבב הקודם.',
          'הזמן נקבע לפי השרת. בקשה שמגיעה אחרי סוף הסבב נדחית ולא מחייבת נקודות.',
          'אפשר להצטרף לסבב פעיל בכל רגע.'
        ]
      },
      {
        title: '4. פרסי האוצרות',
        bullets: [
          'בכל סבב יש בדיוק 10 אוצרות: 1 גדול מאוד, 2 גדולים, 5 בינוניים ו-2 קטנים.',
          'האוצר הגדול מאוד מקבל 34% מקופת הסבב.',
          'כל אוצר גדול מקבל 16%.',
          'כל אוצר בינוני מקבל 6%.',
          'כל אוצר קטן מקבל 2%.',
          'סכומי הפרסים גלויים לפני הסבב ובמהלכו.'
        ]
      },
      {
        title: '5. לחיצות ורמזים',
        bullets: [
          'כל לחיצה בתשלום עולה את מחיר הלחיצה שמופיע באזור המשחק.',
          'הלחיצה מחזירה רמז לאוצר הקרוב ביותר שעדיין לא נמצא בזמן עיבוד השרת.',
          'הרמז כולל מרחק משוער במכוון וכיוון רחב, לא קואורדינטות מדויקות או אזימוט מדויק.',
          'מתחת ל-100 מטר החץ מוסתר. המרחק נשאר משוער ולעולם אינו מדויק.',
          'אם אוצר כבר נמצא על ידי שחקן אחר, הלחיצה הבאה מכוונת לאוצר הקרוב ביותר שנותר.'
        ]
      },
      {
        title: '6. הודעות מדינה ועיר',
        bullets: [
          'כאשר הלחיצה נמצאת במדינת האוצר הקרוב, מוצגת הודעת מדינה נכונה.',
          'כאשר הלחיצה קרובה מספיק לאזור העיר של האוצר, מוצגת הודעת עיר נכונה.',
          'ההודעות הן רמזים בלבד ואינן חושפות נקודה מדויקת.'
        ]
      },
      {
        title: '7. מציאת אוצר',
        bullets: [
          'אוצר נאסף אוטומטית כאשר הלחיצה קרובה מספיק והמפה מוגדלת מספיק.',
          'ב-MVP0 צריך להיות במרחק של עד 25 מטר מהאוצר.',
          'השרת בודק שרמת ההגדלה מספיקה.',
          'בקשת המציאה התקינה הראשונה שמגיעה לשרת זוכה באוצר.',
          'אחרי מציאה מוצג חלון עם שם המקום, קטגוריה, עיר, מדינה, תיאור קצר ותמונה כאשר קיימת.'
        ]
      },
      {
        title: '8. זיכוי פרסים',
        bullets: [
          'פרסי אוצרות שנמצאו ממתינים עד סיום הסבב.',
          'לאחר סגירת הסבב הפרסים מזוכים ליתרת השחקן.',
          'פרסים שלא נמצאו עוברים לקופת הסבב הבא.',
          'כרטיסי האוצרות נשמרים בפרופיל השחקן.'
        ]
      },
      {
        title: '9. קופת הסבב הבא',
        bullets: [
          'סכומי פרסי האוצרות נקבעים בתחילת הסבב.',
          'הקופה שמוצגת בזמן הסבב היא סכום האוצרות שעדיין לא נמצאו, והיא יורדת אחרי כל מציאה.',
          'נקודות שהוצאו בסבב הנוכחי אינן משנות את פרסי הסבב הנוכחי.',
          '10% מכל לחיצה הם עמלת פלטפורמה / ספיגת נקודות.',
          'תצוגת קופת הסבב הבא כוללת 90% מכל לחיצה ואת פרסי האוצרות שעדיין לא נמצאו.'
        ]
      },
      {
        title: '10. מידע ציבורי',
        bullets: [
          'שם המשחק הציבורי גלוי בטבלת המובילים, בעדכונים ובפרופיל הציבורי.',
          'אל תשתמשו בשם אמיתי אם אינכם רוצים להציג אותו לאחרים.',
          'שחקנים אחרים יכולים לפתוח פרופיל ציבורי מטבלת המובילים או מהעדכונים.',
          'הפרופיל הציבורי מציג מספר משחקים, אוצרות שנמצאו, סך פרסים וכרטיסים מסבבים שהסתיימו.',
          'כרטיסים מהסבב הפעיל גלויים רק לבעלים עד סוף הסבב.'
        ]
      },
      {
        title: '11. משחק הוגן',
        bullets: [
          'חשבונות אוטומטיים, פעולות משחק אוטומטיות, שימוש טכני לרעה ושינוי קואורדינטות אסורים.',
          'ריבוי חשבונות שמטרתו לעוות טבלה, פרסים או מדדים עלול להיחסם.',
          'ניצול שגיאות, ניסיון לחשוף נקודות נסתרות ופגיעה בזמינות השירות אסורים.',
          'המפעיל רשאי לחסום חשבונות במקרה של שימוש לרעה ברור.',
          'כתובת IP, נתוני התחברות, דפדפן ומכשיר עשויים לשמש לבדיקה פנימית נגד שימוש לרעה.'
        ]
      },
      {
        title: '12. מצב גרסת הבדיקה',
        bullets: [
          'זוהי גרסת הדפדפן MVP0 - גרסת משחק וטכנולוגיה לבדיקת המוצר.',
          'בגרסה זו אין Telegram, Telegram Stars, תשלומים אמיתיים, משיכה, מטבעות דיגיטליים או פרסים כספיים.',
          'הכללים עשויים להתעדכן במעבר מגרסת הדפדפן MVP0 ל-Telegram MVP1.'
        ]
      }
    ]
  },
  ar: {
    title: 'قواعد لعبة HotCold',
    subtitle: 'صيد كنوز على خريطة العالم: كل نقرة تعطي تلميحًا تقريبيًا إلى أقرب كنز مخفي لم يتم العثور عليه.',
    backToGame: 'العودة إلى اللعبة',
    updated: 'قواعد نسخة المتصفح MVP0',
    sections: [
      {
        title: '1. هدف اللعبة',
        body: 'HotCold هي لعبة صيد كنوز افتراضية على خريطة العالم. الهدف هو العثور على الكنوز بسرعة وكفاءة أكثر من اللاعبين الآخرين.',
        bullets: [
          'في كل جولة توجد 10 كنوز مخفية في نقاط اهتمام حقيقية ومعروفة.',
          'النقرة على الخريطة تستهلك نقاطًا وتعيد مسافة واتجاهًا تقريبيين إلى أقرب كنز لم يتم العثور عليه.',
          'يتضيق البحث تدريجيًا من العالم إلى الدولة ثم المدينة ثم منطقة الشارع ثم المكان الدقيق.'
        ]
      },
      {
        title: '2. النقاط والقيمة',
        bullets: [
          'تستخدم نسخة المتصفح MVP0 نقاطًا ترفيهية فقط.',
          'يمكن إنفاق النقاط على نقرات البحث والفوز بها كمكافآت افتراضية.',
          'لا يمكن سحب النقاط أو نقلها أو بيعها أو تحويلها إلى نقد أو عملات رقمية أو USDT أو جوائز أو أي قيمة مالية.',
          'تعبئة الرصيد الحالية هي نموذج تجريبي ولا تتم معالجة أي دفع حقيقي.'
        ]
      },
      {
        title: '3. الجولات',
        bullets: [
          'تستمر الجولة حتى ساعتين.',
          'تنتهي الجولة مبكرًا إذا تم العثور على الكنوز العشرة كلها.',
          'تبدأ الجولة التالية بعد ساعة من انتهاء الجولة السابقة.',
          'يعتمد التوقيت على وقت الخادم. إذا وصل الطلب بعد انتهاء الجولة، يتم رفضه ولا تُخصم النقاط.',
          'يمكن للاعب الانضمام إلى جولة نشطة في أي وقت.'
        ]
      },
      {
        title: '4. مكافآت الكنوز',
        bullets: [
          'في كل جولة 10 كنوز بالضبط: 1 كبير جدًا، 2 كبير، 5 متوسط و2 صغير.',
          'الكنز الكبير جدًا يحصل على 34% من بنك الجولة.',
          'كل كنز كبير يحصل على 16%.',
          'كل كنز متوسط يحصل على 6%.',
          'كل كنز صغير يحصل على 2%.',
          'مبالغ المكافآت ظاهرة قبل الجولة وأثناءها.'
        ]
      },
      {
        title: '5. النقرات والتلميحات',
        bullets: [
          'كل نقرة مدفوعة تكلف السعر الثابت المعروض في لوحة اللعبة.',
          'تعطي النقرة تلميحًا إلى أقرب كنز لم يتم العثور عليه في لحظة معالجة الخادم.',
          'يتضمن التلميح مسافة تقريبية عمدًا واتجاهًا واسعًا، وليس إحداثيات دقيقة أو اتجاهًا دقيقًا.',
          'تحت 100 متر يختفي سهم الاتجاه. تبقى المسافة تقريبية ولا تصبح دقيقة.',
          'إذا تم العثور على كنز بواسطة لاعب آخر، فإن النقرة التالية تستهدف أقرب كنز متبقٍ.'
        ]
      },
      {
        title: '6. رسائل الدولة والمدينة',
        bullets: [
          'إذا كانت النقرة داخل دولة أقرب كنز، تظهر رسالة الدولة الصحيحة.',
          'إذا كانت النقرة قريبة بما يكفي من منطقة مدينة الكنز، تظهر رسالة المدينة الصحيحة.',
          'هذه الرسائل تلميحات فقط ولا تكشف النقطة الدقيقة.'
        ]
      },
      {
        title: '7. الحصول على الكنز',
        bullets: [
          'يتم الحصول على الكنز تلقائيًا عندما تكون النقرة قريبة بما يكفي ويكون تكبير الخريطة عاليًا بما يكفي.',
          'في MVP0 يجب أن تكون ضمن مسافة 25 مترًا من الكنز.',
          'يتحقق الخادم من الحد الأدنى للتكبير.',
          'أول طلب اكتشاف صالح يصل إلى الخادم يفوز بالكنز.',
          'بعد العثور على كنز، تظهر نافذة باسم المكان والفئة والمدينة والدولة ووصف قصير وصورة عند توفرها.'
        ]
      },
      {
        title: '8. إضافة المكافآت',
        bullets: [
          'تبقى مكافآت الكنوز التي تم العثور عليها معلقة حتى إنهاء الجولة.',
          'بعد إنهاء الجولة تُضاف المكافآت إلى رصيد اللاعب.',
          'المكافآت التي لم يتم العثور عليها تنتقل إلى بنك الجولة التالية.',
          'يتم حفظ بطاقات الكنوز في ملف اللاعب.'
        ]
      },
      {
        title: '9. بنك الجولة التالية',
        bullets: [
          'يتم تثبيت قيم مكافآت الكنوز عند بدء الجولة.',
          'البنك المعروض أثناء الجولة هو قيمة الكنوز التي لم يتم العثور عليها بعد، وينخفض بعد كل اكتشاف.',
          'النقاط المنفقة في الجولة الحالية لا تغير مكافآت الجولة الحالية.',
          '10% من كل نقرة هي رسوم منصة / امتصاص نقاط.',
          'تشمل معاينة بنك الجولة التالية 90% من كل نقرة بالإضافة إلى مكافآت الكنوز التي لم يتم العثور عليها بعد.'
        ]
      },
      {
        title: '10. المعلومات العامة',
        bullets: [
          'اسم اللعبة العام ظاهر في لوحة المتصدرين والنشاط والملف العام.',
          'لا تستخدم اسمك الحقيقي إذا كنت لا تريد عرضه للآخرين.',
          'يمكن للاعبين الآخرين فتح ملفك العام من لوحة المتصدرين أو النشاط.',
          'يعرض الملف العام عدد الألعاب والكنوز المكتشفة وإجمالي المكافآت وبطاقات الجولات المنتهية.',
          'بطاقات الجولة النشطة ظاهرة لصاحبها فقط حتى نهاية الجولة.'
        ]
      },
      {
        title: '11. اللعب النزيه ومنع الإساءة',
        bullets: [
          'الحسابات الآلية وأدوات اللعب الآلي وسوء الاستخدام التقني وتغيير الإحداثيات ممنوعة.',
          'تعدد الحسابات الذي يشوه لوحة المتصدرين أو المكافآت أو المقاييس قد يؤدي إلى الحظر.',
          'استغلال الأخطاء ومحاولة كشف النقاط المخفية والهجمات على توفر الخدمة ممنوعة.',
          'يمكن للمشغل حظر الحسابات عند وجود إساءة واضحة.',
          'قد تُستخدم إشارات عنوان IP والجلسات والمتصفح وبيانات الجهاز في مراجعة داخلية لمنع الإساءة.'
        ]
      },
      {
        title: '12. حالة النسخة التجريبية',
        bullets: [
          'هذه نسخة المتصفح MVP0 - نسخة لعب وتقنية للتحقق من المنتج.',
          'لا توجد في هذه النسخة Telegram أو Telegram Stars أو مدفوعات حقيقية أو سحب أو عملات رقمية أو جوائز مالية.',
          'قد يتم تحسين القواعد عند الانتقال من نسخة المتصفح MVP0 إلى Telegram MVP1.'
        ]
      }
    ]
  }
};

const ADMIN_SETTING_LABELS: Record<Language, Record<string, string>> = {
  en: {
    taps_enabled: 'Taps enabled',
    rounds_enabled: 'Rounds enabled',
    purchases_enabled: 'Purchases enabled'
  },
  ru: {
    taps_enabled: 'Тапы включены',
    rounds_enabled: 'Раунды включены',
    purchases_enabled: 'Покупки включены'
  },
  he: {
    taps_enabled: 'לחיצות פעילות',
    rounds_enabled: 'סבבים פעילים',
    purchases_enabled: 'רכישות פעילות'
  },
  ar: {
    taps_enabled: 'النقرات مفعلة',
    rounds_enabled: 'الجولات مفعلة',
    purchases_enabled: 'المشتريات مفعلة'
  }
};

const ADMIN_ANALYTICS_LABELS: Record<Language, Record<string, string>> = {
  en: {
    app_opened: 'App opened',
    display_name_set: 'Name set',
    geo_blocked: 'Geo blocked',
    welcome_bonus_granted: 'Welcome bonus granted',
    demo_top_up_completed: 'Demo top-up completed',
    round_started: 'Round started',
    round_finalized: 'Round finalized',
    tap_rejected: 'Tap rejected',
    tap_accepted: 'Tap accepted',
    treasure_claimed: 'Treasure claimed'
  },
  ru: {
    app_opened: 'Открытие приложения',
    display_name_set: 'Имя задано',
    geo_blocked: 'Гео заблокировано',
    welcome_bonus_granted: 'Велком-бонус начислен',
    demo_top_up_completed: 'Демо-пополнение выполнено',
    round_started: 'Раунд запущен',
    round_finalized: 'Раунд завершен',
    tap_rejected: 'Тап отклонен',
    tap_accepted: 'Тап принят',
    treasure_claimed: 'Сокровище найдено'
  },
  he: {
    app_opened: 'האפליקציה נפתחה',
    display_name_set: 'השם נקבע',
    geo_blocked: 'חסימה גיאוגרפית',
    welcome_bonus_granted: 'בונוס כניסה הוענק',
    demo_top_up_completed: 'טעינת דמו הושלמה',
    round_started: 'סבב התחיל',
    round_finalized: 'סבב הסתיים',
    tap_rejected: 'לחיצה נדחתה',
    tap_accepted: 'לחיצה התקבלה',
    treasure_claimed: 'אוצר נמצא'
  },
  ar: {
    app_opened: 'تم فتح التطبيق',
    display_name_set: 'تم تعيين الاسم',
    geo_blocked: 'تم الحظر جغرافيًا',
    welcome_bonus_granted: 'تم منح مكافأة الترحيب',
    demo_top_up_completed: 'اكتمل شحن الرصيد التجريبي',
    round_started: 'بدأت الجولة',
    round_finalized: 'انتهت الجولة',
    tap_rejected: 'تم رفض النقرة',
    tap_accepted: 'تم قبول النقرة',
    treasure_claimed: 'تم العثور على كنز'
  }
};

const ADMIN_DASHBOARD_LABELS: Record<Language, Record<string, string>> = {
  en: {
    dashboard: 'Dashboard',
    liveNow: 'Live now',
    activePlayers15m: 'Active players · 15m',
    activeSessions15m: 'Active sessions · 15m',
    taps15m: 'Taps · 15m',
    taps24h: 'Taps · 24h',
    claims24h: 'Claims · 24h',
    spent24h: 'Spent · 24h',
    topups24h: 'Top-ups · 24h',
    newPlayers24h: 'New players · 24h',
    currentRound: 'Current round',
    noActiveRound: 'No active round',
    found: 'Found',
    hidden: 'Hidden',
    endsAt: 'Ends',
    hourlyDynamics: 'Last 24 hours',
    dailyDynamics: 'Last 14 days',
    funnel7d: 'Funnel · 7 days',
    economy7d: 'Economy · 7 days',
    platformFee: 'Platform fee',
    nextBankContribution: 'Next bank contribution',
    totalSpent: 'Total spent',
    topPlayers: 'Top players',
    latestRounds: 'Latest rounds',
    rewards: 'Rewards',
    riskSignals: 'Risk signals',
    sharedIpGroups: 'Shared IP groups',
    sharedIpPlayers: 'Players on shared IPs',
    rejectedTaps24h: 'Rejected taps · 24h',
    blockedPlayers: 'Blocked players',
    devices: 'Devices',
    platforms: 'Platforms',
    languages: 'Languages',
    timezones: 'Timezones',
    generatedAt: 'Updated',
    events: 'Events'
  },
  ru: {
    dashboard: 'Дашборд',
    liveNow: 'Сейчас',
    activePlayers15m: 'Активные игроки · 15 мин',
    activeSessions15m: 'Активные сессии · 15 мин',
    taps15m: 'Тапы · 15 мин',
    taps24h: 'Тапы · 24 часа',
    claims24h: 'Находки · 24 часа',
    spent24h: 'Потрачено · 24 часа',
    topups24h: 'Пополнения · 24 часа',
    newPlayers24h: 'Новые игроки · 24 часа',
    currentRound: 'Текущий раунд',
    noActiveRound: 'Активного раунда нет',
    found: 'Найдено',
    hidden: 'Скрыто',
    endsAt: 'Конец',
    hourlyDynamics: 'Последние 24 часа',
    dailyDynamics: 'Последние 14 дней',
    funnel7d: 'Воронка · 7 дней',
    economy7d: 'Экономика · 7 дней',
    platformFee: 'Комиссия платформы',
    nextBankContribution: 'Вклад в следующий банк',
    totalSpent: 'Потрачено всего',
    topPlayers: 'Топ игроков',
    latestRounds: 'Последние раунды',
    rewards: 'Награды',
    riskSignals: 'Риск-сигналы',
    sharedIpGroups: 'Группы с общим IP',
    sharedIpPlayers: 'Игроки на общих IP',
    rejectedTaps24h: 'Отклоненные тапы · 24 часа',
    blockedPlayers: 'Заблокированные игроки',
    devices: 'Устройства',
    platforms: 'Платформы',
    languages: 'Языки',
    timezones: 'Часовые пояса',
    generatedAt: 'Обновлено',
    events: 'События'
  },
  he: {
    dashboard: 'לוח נתונים',
    liveNow: 'עכשיו',
    activePlayers15m: 'שחקנים פעילים · 15 דק׳',
    activeSessions15m: 'סשנים פעילים · 15 דק׳',
    taps15m: 'לחיצות · 15 דק׳',
    taps24h: 'לחיצות · 24 שעות',
    claims24h: 'מציאות · 24 שעות',
    spent24h: 'הוצאה · 24 שעות',
    topups24h: 'טעינות · 24 שעות',
    newPlayers24h: 'שחקנים חדשים · 24 שעות',
    currentRound: 'הסבב הנוכחי',
    noActiveRound: 'אין סבב פעיל',
    found: 'נמצאו',
    hidden: 'מוסתרים',
    endsAt: 'מסתיים',
    hourlyDynamics: '24 השעות האחרונות',
    dailyDynamics: '14 הימים האחרונים',
    funnel7d: 'משפך · 7 ימים',
    economy7d: 'כלכלה · 7 ימים',
    platformFee: 'עמלת פלטפורמה',
    nextBankContribution: 'תרומה לקופה הבאה',
    totalSpent: 'סה״כ הוצאה',
    topPlayers: 'שחקנים מובילים',
    latestRounds: 'סבבים אחרונים',
    rewards: 'פרסים',
    riskSignals: 'סימני סיכון',
    sharedIpGroups: 'קבוצות IP משותף',
    sharedIpPlayers: 'שחקנים על IP משותף',
    rejectedTaps24h: 'לחיצות שנדחו · 24 שעות',
    blockedPlayers: 'שחקנים חסומים',
    devices: 'מכשירים',
    platforms: 'פלטפורמות',
    languages: 'שפות',
    timezones: 'אזורי זמן',
    generatedAt: 'עודכן',
    events: 'אירועים'
  },
  ar: {
    dashboard: 'لوحة البيانات',
    liveNow: 'الآن',
    activePlayers15m: 'لاعبون نشطون · 15 دقيقة',
    activeSessions15m: 'جلسات نشطة · 15 دقيقة',
    taps15m: 'نقرات · 15 دقيقة',
    taps24h: 'نقرات · 24 ساعة',
    claims24h: 'اكتشافات · 24 ساعة',
    spent24h: 'تم إنفاقه · 24 ساعة',
    topups24h: 'شحنات · 24 ساعة',
    newPlayers24h: 'لاعبون جدد · 24 ساعة',
    currentRound: 'الجولة الحالية',
    noActiveRound: 'لا توجد جولة نشطة',
    found: 'تم العثور',
    hidden: 'مخفية',
    endsAt: 'تنتهي',
    hourlyDynamics: 'آخر 24 ساعة',
    dailyDynamics: 'آخر 14 يومًا',
    funnel7d: 'القمع · 7 أيام',
    economy7d: 'الاقتصاد · 7 أيام',
    platformFee: 'رسوم المنصة',
    nextBankContribution: 'مساهمة البنك التالي',
    totalSpent: 'إجمالي الإنفاق',
    topPlayers: 'أفضل اللاعبين',
    latestRounds: 'آخر الجولات',
    rewards: 'المكافآت',
    riskSignals: 'إشارات المخاطر',
    sharedIpGroups: 'مجموعات IP مشتركة',
    sharedIpPlayers: 'لاعبون على IP مشترك',
    rejectedTaps24h: 'نقرات مرفوضة · 24 ساعة',
    blockedPlayers: 'لاعبون محظورون',
    devices: 'الأجهزة',
    platforms: 'المنصات',
    languages: 'اللغات',
    timezones: 'المناطق الزمنية',
    generatedAt: 'تم التحديث',
    events: 'الأحداث'
  }
};

type Toast = {
  title: string;
  tone?: 'good' | 'bad' | 'neutral';
  actionLabel?: string;
  onAction?: () => void;
};

type FoundTreasure = {
  rewardPoints: number;
  treasureCategory: string;
  place: TreasurePlace;
  foundAt: string;
};

const DISTANCE_UNITS: Record<Language, { nearby: string; meter: string; kilometer: string }> = {
  en: { nearby: 'nearby', meter: 'm', kilometer: 'km' },
  ru: { nearby: 'рядом', meter: 'м', kilometer: 'км' },
  he: { nearby: 'קרוב', meter: 'מ׳', kilometer: 'ק״מ' },
  ar: { nearby: 'قريب', meter: 'م', kilometer: 'كم' }
};

const CATEGORY_LABELS: Record<Language, Record<string, string>> = {
  en: { very_large: 'Very large', large: 'Large', medium: 'Medium', small: 'Small' },
  ru: { very_large: 'Очень большое', large: 'Большое', medium: 'Среднее', small: 'Маленькое' },
  he: { very_large: 'גדול מאוד', large: 'גדול', medium: 'בינוני', small: 'קטן' },
  ar: { very_large: 'كبير جدًا', large: 'كبير', medium: 'متوسط', small: 'صغير' }
};

const COUNTRY_LABELS: Record<Language, Record<Country, string>> = {
  en: {
    Kazakhstan: 'Kazakhstan',
    Armenia: 'Armenia',
    Georgia: 'Georgia',
    Serbia: 'Serbia',
    Montenegro: 'Montenegro',
    Argentina: 'Argentina',
    Brazil: 'Brazil',
    Mexico: 'Mexico',
    Israel: 'Israel',
    'United Arab Emirates': 'United Arab Emirates',
    Thailand: 'Thailand',
    Vietnam: 'Vietnam',
    Other: 'Other'
  },
  ru: {
    Kazakhstan: 'Казахстан',
    Armenia: 'Армения',
    Georgia: 'Грузия',
    Serbia: 'Сербия',
    Montenegro: 'Черногория',
    Argentina: 'Аргентина',
    Brazil: 'Бразилия',
    Mexico: 'Мексика',
    Israel: 'Израиль',
    'United Arab Emirates': 'ОАЭ',
    Thailand: 'Таиланд',
    Vietnam: 'Вьетнам',
    Other: 'Другое'
  },
  he: {
    Kazakhstan: 'קזחסטן',
    Armenia: 'ארמניה',
    Georgia: 'גאורגיה',
    Serbia: 'סרביה',
    Montenegro: 'מונטנגרו',
    Argentina: 'ארגנטינה',
    Brazil: 'ברזיל',
    Mexico: 'מקסיקו',
    Israel: 'ישראל',
    'United Arab Emirates': 'איחוד האמירויות',
    Thailand: 'תאילנד',
    Vietnam: 'וייטנאם',
    Other: 'אחר'
  },
  ar: {
    Kazakhstan: 'كازاخستان',
    Armenia: 'أرمينيا',
    Georgia: 'جورجيا',
    Serbia: 'صربيا',
    Montenegro: 'الجبل الأسود',
    Argentina: 'الأرجنتين',
    Brazil: 'البرازيل',
    Mexico: 'المكسيك',
    Israel: 'إسرائيل',
    'United Arab Emirates': 'الإمارات العربية المتحدة',
    Thailand: 'تايلاند',
    Vietnam: 'فيتنام',
    Other: 'أخرى'
  }
};

function formatPoints(value: number | undefined, language: Language): string {
  return new Intl.NumberFormat(languageMeta(language).locale).format(value ?? 0);
}

function formatAdminSetting(setting: string, language: Language): string {
  return ADMIN_SETTING_LABELS[language][setting] ?? setting.replaceAll('_', ' ');
}

function formatAnalyticsEvent(eventName: string, language: Language): string {
  return ADMIN_ANALYTICS_LABELS[language][eventName] ?? eventName.replaceAll('_', ' ');
}

function dashboardLabel(language: Language, key: string): string {
  return ADMIN_DASHBOARD_LABELS[language][key] ?? key;
}

function browserTelemetryPayload() {
  const nav = window.navigator;
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: nav.language,
    languages: Array.from(nav.languages ?? []),
    platform: nav.platform,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    hardwareConcurrency: nav.hardwareConcurrency,
    maxTouchPoints: nav.maxTouchPoints
  };
}

function formatDistance(meters: number | null | undefined, language: Language): string {
  const units = DISTANCE_UNITS[language];
  const formatter = new Intl.NumberFormat(languageMeta(language).locale);
  if (meters == null) return units.nearby;
  if (meters >= 1000) return `~${formatter.format(Math.round(meters / 1000))} ${units.kilometer}`;
  return `~${formatter.format(meters)} ${units.meter}`;
}

function formatDateTime(value: string, language: Language): string {
  return new Intl.DateTimeFormat(languageMeta(language).locale, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value));
}

function categoryLabel(category: string, language: Language): string {
  return CATEGORY_LABELS[language][category] ?? category;
}

function placeCategoryLabel(category: string): string {
  return category.replace(/_/g, ' ');
}

function countryLabel(country: Country, language: Language): string {
  return COUNTRY_LABELS[language][country] ?? country;
}

function compactValue(value: unknown, fallback: string): string {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || fallback;
  if (value == null || value === '') return fallback;
  return String(value);
}

function formatScreen(player: any, fallback: string): string {
  if (!player.screen_width || !player.screen_height) return fallback;
  const viewport = player.viewport_width && player.viewport_height
    ? ` / ${player.viewport_width}x${player.viewport_height}`
    : '';
  const ratio = player.device_pixel_ratio ? ` @${player.device_pixel_ratio}x` : '';
  return `${player.screen_width}x${player.screen_height}${viewport}${ratio}`;
}

function shortDate(value: string, language: Language): string {
  return new Intl.DateTimeFormat(languageMeta(language).locale, { month: 'short', day: 'numeric' }).format(new Date(value));
}

function shortHour(value: string, language: Language): string {
  return new Intl.DateTimeFormat(languageMeta(language).locale, { hour: '2-digit' }).format(new Date(value));
}

function errorTitle(error: unknown, language: Language, fallbackKey: TranslationKey): string {
  if (error instanceof ApiError) {
    const known: Partial<Record<string, TranslationKey>> = {
      account_blocked: 'adminRequestFailed',
      age_confirmation_required: 'ageConfirm',
      display_name_required: 'displayNameRequired',
      display_name_taken: 'displayNameTaken',
      geo_blocked: 'geoBlocked',
      insufficient_points: 'insufficientPoints',
      invalid_display_name: 'invalidDisplayName',
      onboarding_required: 'onboardingRequired',
      rate_limited: 'tapCooldown',
      round_not_active: 'roundNotActive'
    };
    const key = known[error.code];
    if (key) return t(language, key);
  }
  return error instanceof Error ? error.message : t(language, fallbackKey);
}

function useCountdown(target?: string | null): string {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  if (!target) return '--:--';
  const diff = Math.max(0, new Date(target).getTime() - now);
  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1000);
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function roundFromState(state: RoundState | null) {
  if (!state) return null;
  if (state.state === 'active') return state.round;
  if (state.state === 'intermission') return state.previousRound;
  return null;
}

function LanguageSwitcher({ language, className = '', onLanguage }: { language: Language; className?: string; onLanguage: (language: Language) => void }) {
  return (
    <label className={`languageSwitcher ${className}`.trim()} aria-label={t(language, 'language')}>
      <Globe2 size={16} />
      <select value={language} onChange={(event) => onLanguage(event.target.value as Language)}>
        {LANGUAGES.map((item) => (
          <option key={item.code} value={item.code}>{item.label}</option>
        ))}
      </select>
    </label>
  );
}

function RulesPage({ language }: { language: Language }) {
  const content = RULES_CONTENT[language];
  return (
    <main className="rulesPage">
      <section className="rulesHero">
        <div>
          <div className="kicker">{content.updated}</div>
          <h1>{content.title}</h1>
          <p>{content.subtitle}</p>
        </div>
        <a className="rulesBackButton" href="/">
          <MapPin size={18} />
          {content.backToGame}
        </a>
      </section>

      <section className="rulesGrid">
        {content.sections.map((section) => (
          <article className="rulesSection" key={section.title}>
            <h2>{section.title}</h2>
            {section.body ? <p>{section.body}</p> : null}
            <ul>
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </article>
        ))}
      </section>
    </main>
  );
}

function Onboarding({
  player,
  config,
  language,
  tt,
  onLanguage,
  onPlayer,
  onToast
}: {
  player: Player;
  config: AppConfig;
  language: Language;
  tt: T;
  onLanguage: (language: Language) => void;
  onPlayer: (player: Player) => void;
  onToast: (toast: Toast) => void;
}) {
  const [displayName, setDisplayName] = useState(player.displayName ?? '');
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const saveName = async () => {
    setBusy(true);
    try {
      const result = await api.setDisplayName(displayName);
      onPlayer(result.player);
      onToast({ title: tt('nameSaved'), tone: 'good' });
    } catch (error) {
      onToast({ title: errorTitle(error, language, 'nameFailed'), tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  const saveEligibility = async () => {
    setBusy(true);
    try {
      const result = await api.setEligibility(ageConfirmed, country === 'Other' ? null : country);
      onPlayer(result.player);
      onToast({ title: `${formatPoints(config.welcomeBonusPoints, language)} ${tt('pointsCredited')}`, tone: 'good' });
    } catch (error) {
      onToast({ title: errorTitle(error, language, 'eligibilityFailed'), tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="onboarding">
      <div className="onboardingPanel">
        <div className="brandRow">
          <div className="brandMark"><Compass size={22} /></div>
          <div>
            <div className="brandTitle">HotCold</div>
            <div className="brandSubtitle">{tt('treasureHuntDemo')}</div>
          </div>
        </div>

        <LanguageSwitcher language={language} className="embeddedLanguageSwitcher onboardingPanelLanguage" onLanguage={onLanguage} />

        <a className="onboardingRulesLink" href="/rules">
          <ListChecks size={16} />
          {RULES_CONTENT[language].title}
        </a>

        <div className="onboardingStep">
          <label htmlFor="displayName">{tt('publicGameName')}</label>
          <div className="inlineForm">
            <input
              id="displayName"
              value={displayName}
              maxLength={24}
              placeholder={tt('explorerName')}
              onChange={(event) => setDisplayName(event.target.value)}
              disabled={busy}
            />
            <button type="button" onClick={saveName} disabled={busy || displayName.trim().length < 3}>
              <User size={18} />
              {tt('save')}
            </button>
          </div>
          <p>{tt('visibleNameWarning')}</p>
        </div>

        <div className="onboardingStep">
          <label htmlFor="country">{tt('country')}</label>
          <select id="country" value={country} onChange={(event) => setCountry(event.target.value as Country)} disabled={!player.displayName || busy}>
            {COUNTRIES.map((item) => (
              <option key={item} value={item}>{countryLabel(item, language)}</option>
            ))}
          </select>
          <label className="checkRow">
            <input type="checkbox" checked={ageConfirmed} onChange={(event) => setAgeConfirmed(event.target.checked)} disabled={!player.displayName || busy} />
            <span>{tt('ageConfirm')}</span>
          </label>
          <button type="button" className="wideButton" onClick={saveEligibility} disabled={!player.displayName || !ageConfirmed || busy}>
            <ShieldCheck size={18} />
            {tt('enterRound')}
          </button>
        </div>
      </div>
    </section>
  );
}

function GameMap({
  config,
  roundState,
  player,
  language,
  tt,
  onTapResult,
  onInsufficientFunds,
  onToast
}: {
  config: AppConfig;
  roundState: RoundState | null;
  player: Player;
  language: Language;
  tt: T;
  onTapResult: (response: TapResponse) => void;
  onInsufficientFunds: () => void;
  onToast: (toast: Toast) => void;
}) {
  const mapNode = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const [busy, setBusy] = useState(false);
  const [mapLayer, setMapLayer] = useState<MapLayer>('map');
  const activeRound = roundState?.state === 'active' ? roundState.round : null;

  useEffect(() => {
    if (!mapNode.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapNode.current,
      style: mapStyleForLayer(config.map.styleUrl, mapLayer),
      center: [37.6173, 55.7558],
      zoom: 2.1,
      maxPitch: 0,
      dragRotate: false,
      pitchWithRotate: false,
      renderWorldCopies: false,
      attributionControl: false
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right');
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
    mapRef.current = map;
    return () => {
      markerRef.current?.remove();
      map.remove();
      mapRef.current = null;
    };
  }, [config.map.styleUrl]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    const bearing = map.getBearing();
    const pitch = map.getPitch();
    map.setStyle(mapStyleForLayer(config.map.styleUrl, mapLayer));
    map.once('styledata', () => {
      map.jumpTo({ center, zoom, bearing, pitch });
    });
  }, [config.map.styleUrl, mapLayer]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handler = async (event: maplibregl.MapMouseEvent) => {
      if (!activeRound || busy) return;
      if (!player.onboardingComplete) {
        onToast({ title: tt('completeOnboarding'), tone: 'bad' });
        return;
      }
      setBusy(true);
      try {
        markerRef.current?.remove();
        const tapLat = event.lngLat.lat;
        const tapLng = normalizeLng(event.lngLat.lng);
        if (!validTapPoint(tapLat, tapLng)) {
          onToast({ title: tt('invalidMapPoint'), tone: 'bad' });
          return;
        }
        markerRef.current = new maplibregl.Marker({ color: '#f4b840' })
          .setLngLat([tapLng, tapLat] as LngLatLike)
          .addTo(map);
        const response = await api.tap(activeRound.id, tapLat, tapLng, map.getZoom());
        onTapResult(response);
        if (response.tap.claimResult === 'success') {
          return;
        }
        if (response.tap.cityHit) {
          onToast({
            title: `${tt('cityHit')}: ${response.tap.cityName ?? ''}`,
            tone: 'good'
          });
        } else if (response.tap.countryHit) {
          onToast({
            title: `${tt('countryHit')}: ${response.tap.countryName ?? ''}`,
            tone: 'good'
          });
        } else {
          onToast({ title: `${formatDistance(response.tap.roundedDistanceMeters, language)} ${tt('nearestTreasure')}`, tone: 'neutral' });
        }
      } catch (error) {
        if (error instanceof ApiError && error.code === 'insufficient_points') {
          onToast({
            title: tt('insufficientPoints'),
            tone: 'bad',
            actionLabel: tt('topUpBalance'),
            onAction: onInsufficientFunds
          });
          return;
        }
        onToast({ title: errorTitle(error, language, 'tapFailed'), tone: 'bad' });
      } finally {
        window.setTimeout(() => setBusy(false), 250);
      }
    };
    map.on('click', handler);
    return () => {
      map.off('click', handler);
    };
  }, [activeRound, busy, language, onInsufficientFunds, onTapResult, onToast, player.onboardingComplete, tt]);

  return (
    <div className="mapShell">
      <div ref={mapNode} className="map" />
      <div className="mapLayerToggle" aria-label="Map layer">
        <button
          type="button"
          className={mapLayer === 'map' ? 'active' : ''}
          onClick={() => setMapLayer('map')}
        >
          {tt('mapLayer')}
        </button>
        <button
          type="button"
          className={mapLayer === 'satellite' ? 'active' : ''}
          onClick={() => setMapLayer('satellite')}
        >
          {tt('satelliteLayer')}
        </button>
      </div>
      {mapLayer === 'satellite' ? (
        <div className="eoxAttribution">Sentinel-2 cloudless 2024 by EOX · Copernicus Sentinel data</div>
      ) : null}
      {busy ? (
        <div className="mapBusy">
          <RefreshCw size={18} />
          {tt('checking')}
        </div>
      ) : null}
      {roundState?.state === 'active' && roundState.lastClue ? (
        <div className="clueCard">
          <div className="clueArrow" style={{ transform: `rotate(${roundState.lastClue.directionDegrees ?? 0}deg)` }}>
            {roundState.lastClue.directionShown ? <span className="directionCompass" aria-hidden="true" /> : <MapPin size={42} />}
          </div>
          <div>
            <div className="clueDistance">{formatDistance(roundState.lastClue.roundedDistanceMeters, language)}</div>
            <div className="clueMeta">{roundState.lastClue.directionShown ? tt('directionApprox') : tt('arrowHidden')}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RoundPanel({
  roundState,
  player,
  config,
  language,
  tt,
  onLanguage,
  onProfile,
  onRefresh
}: {
  roundState: RoundState | null;
  player: Player | null;
  config: AppConfig | null;
  language: Language;
  tt: T;
  onLanguage: (language: Language) => void;
  onProfile: () => void;
  onRefresh: () => void;
}) {
  const round = roundFromState(roundState);
  const endsIn = useCountdown(roundState?.state === 'active' ? roundState.round.endsAt : null);
  const nextIn = useCountdown(roundState?.state === 'intermission' ? roundState.nextStartsAt : null);
  const [rewardDetailsOpen, setRewardDetailsOpen] = useState(false);
  const [revealedOpen, setRevealedOpen] = useState(false);

  return (
    <aside className="sidePanel">
      <div className="panelTop">
        <div>
          <div className="kicker">{tt('round')}</div>
          <h1>{round ? `#${round.sequence}` : tt('waiting')}</h1>
        </div>
        <div className="panelActions">
          <LanguageSwitcher language={language} className="embeddedLanguageSwitcher panelActionLanguage" onLanguage={onLanguage} />
          <a className="rulesTextButton" href="/rules" aria-label={tt('rules')}>
            <ListChecks size={18} />
            <span>{tt('rules')}</span>
          </a>
          <button className="iconButton" type="button" onClick={onProfile} aria-label={tt('profile')}>
            <User size={18} />
          </button>
          <button className="iconButton" type="button" onClick={onRefresh} aria-label={tt('refresh')}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <div className="panelUtilityRow">
        <LanguageSwitcher language={language} className="embeddedLanguageSwitcher panelLanguageSwitcher" onLanguage={onLanguage} />
      </div>

      <div className="statsGrid">
        <div className="metric">
          <Wallet size={18} />
          <span>{tt('balance')}</span>
          <strong>{formatPoints(player?.pointsBalance, language)}</strong>
        </div>
        <button
          className="metric metricButton"
          type="button"
          aria-expanded={rewardDetailsOpen}
          aria-controls="reward-details"
          onClick={() => setRewardDetailsOpen((current) => !current)}
        >
          <Trophy size={18} />
          <span>{tt('bank')}</span>
          <strong>{formatPoints(round?.bankPoints, language)}</strong>
        </button>
        <div className="metric">
          <Coins size={18} />
          <span>{tt('tap')}</span>
          <strong>{formatPoints(config?.tapPricePoints, language)}</strong>
        </div>
        <div className="metric">
          <Play size={18} />
          <span>{roundState?.state === 'intermission' ? tt('next') : tt('timer')}</span>
          <strong>{roundState?.state === 'intermission' ? nextIn : endsIn}</strong>
        </div>
      </div>

      {round ? (
        <div id="reward-details" className={`rewardBlock${rewardDetailsOpen ? ' isOpen' : ''}`}>
          <div className="sectionTitle">
            <Sparkles size={17} />
            {tt('treasuresLeft')}
          </div>
          {['very_large', 'large', 'medium', 'small'].map((category) => (
            <div className="rewardRow" key={category}>
              <span>{categoryLabel(category, language)}</span>
              <b>{round.remaining[category] ?? 0}</b>
              <em>{formatPoints(round.rewards[category], language)}</em>
            </div>
          ))}
          <div className="futureBank">
            <span>{tt('nextBankPreview')}</span>
            <strong>{formatPoints(round.nextBankPreview, language)}</strong>
          </div>
        </div>
      ) : (
        <div className="emptyPanel">{roundState?.state === 'empty' ? roundState.message : tt('loadingRound')}</div>
      )}

      {roundState?.state === 'intermission' ? (
        <div className={`revealedList${revealedOpen ? ' isOpen' : ''}`}>
          <button
            type="button"
            className="sectionTitle revealedToggle"
            aria-expanded={revealedOpen}
            onClick={() => setRevealedOpen((current) => !current)}
          >
            <MapPin size={17} />
            {tt('revealedTreasures')}
            <em>{roundState.revealedTreasures.length}</em>
          </button>
          <div className="revealedRows">
            {roundState.revealedTreasures.map((treasure) => (
              <div className="revealedRow" key={treasure.id}>
                <strong>{treasure.poi.name}</strong>
                <span>{treasure.poi.city}, {treasure.poi.country}</span>
                <em>{treasure.finderDisplayName ?? tt('notFound')} · {formatPoints(treasure.rewardPoints, language)}</em>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </aside>
  );
}

function feedMessage(event: PublicFeedEvent, language: Language, tt: T): string {
  const metadata = event.metadata as Record<string, unknown>;
  const displayName = metadata.display_name;
  const category = metadata.category;
  const reward = metadata.reward_points;
  if (typeof displayName === 'string' && typeof category === 'string' && typeof reward === 'number') {
    return `${tt('treasureFound')}: ${displayName} · ${categoryLabel(category, language)} · ${formatPoints(reward, language)}`;
  }
  return event.message;
}

function ActivityPanel({
  roundState,
  language,
  tt,
  open,
  onClose,
  onToggle,
  onPlayerProfile
}: {
  roundState: RoundState | null;
  language: Language;
  tt: T;
  open: boolean;
  onClose: () => void;
  onToggle: () => void;
  onPlayerProfile: (playerId: string) => void;
}) {
  const feed = roundState && 'feed' in roundState ? roundState.feed : [];
  const leaderboard = roundState && 'leaderboard' in roundState ? roundState.leaderboard : [];
  const participantCount = roundFromState(roundState)?.participantsCount ?? 0;
  const activityPanelId = 'activity-panel';

  const openPlayerProfile = (playerId: string) => {
    onClose();
    onPlayerProfile(playerId);
  };

  return (
    <>
      <button
        type="button"
        className="activityToggle"
        aria-controls={activityPanelId}
        aria-expanded={open}
        onClick={onToggle}
      >
        <Crown size={18} />
        <span>{tt('leaderboard')}</span>
        <em>{formatPoints(participantCount, language)}</em>
      </button>

      <aside id={activityPanelId} className={`activityPanel${open ? ' isOpen' : ''}`}>
        <button type="button" className="activityCloseButton" aria-label={tt('close')} onClick={onClose}>
          <X size={18} />
        </button>

        <div className="sectionTitle">
          <Crown size={17} />
          {tt('leaderboard')}
        </div>
        <div className="participantCount">{formatPoints(participantCount, language)} {tt('participants')}</div>
        <div className="leaderboard">
          {leaderboard.length ? leaderboard.map((row) => (
            <div className="leaderRow" key={row.playerId}>
              <span>{row.rank}</span>
              <strong>{row.displayName}</strong>
              <em>{formatPoints(row.pendingRewards, language)}</em>
              <button type="button" className="profileActionLink" onClick={() => openPlayerProfile(row.playerId)}>
                <User size={13} />
                {tt('profile')}
              </button>
            </div>
          )) : <div className="emptyLine">{tt('noClaimsYet')}</div>}
        </div>

        <div className="sectionTitle feedTitle">
          <ListChecks size={17} />
          {tt('feed')}
        </div>
        <div className="feed">
          {feed.length ? feed.map((event) => (
            <div className="feedRow" key={event.id}>
              <span>{new Date(event.createdAt).toLocaleTimeString(languageMeta(language).locale, { hour: '2-digit', minute: '2-digit' })}</span>
              <strong>{feedMessage(event, language, tt)}</strong>
              {event.playerId ? (
                <button type="button" className="profileActionLink feedProfileAction" onClick={() => openPlayerProfile(event.playerId!)}>
                  <User size={13} />
                  {tt('profile')}
                </button>
              ) : null}
            </div>
          )) : <div className="emptyLine">{tt('noEventsYet')}</div>}
        </div>
      </aside>
    </>
  );
}

function PlacePhoto({ place, tt }: { place: TreasurePlace; tt: T }) {
  const [failed, setFailed] = useState(false);
  if (!place.imageUrl || failed) {
    return (
      <div className="placePhotoFallback">
        <ImageOff size={28} />
        <span>{tt('photoUnavailable')}</span>
      </div>
    );
  }
  return (
    <img
      className="placePhoto"
      src={place.imageUrl}
      alt={place.name}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function TreasureFoundModal({
  treasure,
  language,
  tt,
  onClose
}: {
  treasure: FoundTreasure;
  language: Language;
  tt: T;
  onClose: () => void;
}) {
  const place = treasure.place;
  return (
    <div className="modalBackdrop" role="presentation">
      <section className="treasureModal" role="dialog" aria-modal="true" aria-labelledby="treasureFoundTitle">
        <button type="button" className="modalClose" onClick={onClose} aria-label={tt('close')}>
          <X size={18} />
        </button>
        <PlacePhoto place={place} tt={tt} />
        <div className="treasureModalBody">
          <div className="kicker">{tt('treasureFound')}</div>
          <h2 id="treasureFoundTitle">{place.name}</h2>
          <div className="placeMeta">
            {placeCategoryLabel(place.category)} · {place.city}, {place.country}
          </div>
          <div className="rewardBadge">
            +{formatPoints(treasure.rewardPoints, language)} {tt('pointsAfterRound')}
          </div>
          <h3>{tt('placeInfo')}</h3>
          <p>{place.description ?? `${place.name}, ${place.city}, ${place.country}`}</p>
          <div className="sourceLine">
            <span>{place.imageAttribution ?? tt('foundPlace')}</span>
            {place.sourceUrl ? (
              <a href={place.sourceUrl} target="_blank" rel="noreferrer">
                <ExternalLink size={15} />
                {tt('source')}
              </a>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

function ProfileModal({
  profile,
  busy,
  language,
  tt,
  onClose
}: {
  profile: PlayerProfile | null;
  busy: boolean;
  language: Language;
  tt: T;
  onClose: () => void;
}) {
  const cards = profile?.cards ?? [];
  return (
    <div className="modalBackdrop" role="presentation">
      <section className="profileModal" role="dialog" aria-modal="true" aria-labelledby="profileTitle">
        <div className="profileHeader">
          <div>
            <div className="kicker">{tt('profile')}</div>
            <h2 id="profileTitle">{profile?.displayName ?? tt('profile')}</h2>
          </div>
          <button type="button" className="modalCloseInline" onClick={onClose} aria-label={tt('close')}>
            <X size={18} />
          </button>
        </div>

        {busy ? (
          <div className="profileEmpty">
            <RefreshCw size={18} />
            {tt('load')}
          </div>
        ) : profile ? (
          <>
            <div className="profileStats">
              <div className="profileStat">
                <span>{tt('gamesPlayed')}</span>
                <strong>{formatPoints(profile.gamesCount, language)}</strong>
              </div>
              <div className="profileStat">
                <span>{tt('treasuresFoundCount')}</span>
                <strong>{formatPoints(profile.treasuresFound, language)}</strong>
              </div>
              <div className="profileStat">
                <span>{tt('totalWon')}</span>
                <strong>{formatPoints(profile.totalRewardPoints, language)}</strong>
              </div>
            </div>
            {!profile.isOwnProfile ? <div className="profileNotice">{tt('publicCardsAfterRound')}</div> : null}
            <div className="profileSectionTitle">{tt('treasureCards')}</div>
            {cards.length ? (
              <div className="treasureCardGrid">
                {cards.map((card) => (
                  <article className="treasureCard" key={card.id}>
                    <PlacePhoto place={card.place} tt={tt} />
                    <div className="treasureCardBody">
                      <strong>{card.place.name}</strong>
                      <span>{placeCategoryLabel(card.place.category)} · {card.place.city}, {card.place.country}</span>
                      <p>{card.place.description ?? `${card.place.city}, ${card.place.country}`}</p>
                      <div className="treasureCardFooter">
                        <em>{formatPoints(card.rewardPoints, language)}</em>
                        <span>{tt('foundAt')}: {formatDateTime(card.foundAt, language)}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="profileEmpty compact">{tt('noTreasureCards')}</div>
            )}
          </>
        ) : (
          <div className="profileEmpty">{tt('loadFailed')}</div>
        )}
      </section>
    </div>
  );
}

function TopUpModal({
  language,
  tt,
  busy,
  onSubmit,
  onClose
}: {
  language: Language;
  tt: T;
  busy: boolean;
  onSubmit: (amount: number) => void;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState(500);

  return (
    <div className="modalBackdrop" role="presentation">
      <section className="topUpModal" role="dialog" aria-modal="true" aria-labelledby="topUpTitle">
        <h2 id="topUpTitle">{tt('topUpTitle')}</h2>
        <p>{tt('topUpHint')}</p>
        <label htmlFor="topUpAmount">{tt('topUpAmount')}</label>
        <input
          id="topUpAmount"
          type="number"
          min={20}
          max={100000}
          step={20}
          value={amount}
          onChange={(event) => setAmount(Number(event.target.value))}
          disabled={busy}
        />
        <div className="modalActions">
          <button type="button" className="secondaryButton" onClick={onClose} disabled={busy}>
            {tt('topUpCancel')}
          </button>
          <button type="button" onClick={() => onSubmit(amount)} disabled={busy || !Number.isFinite(amount) || amount < 20 || amount > 100000}>
            <Wallet size={18} />
            {tt('topUpSubmit')} · {formatPoints(amount, language)}
          </button>
        </div>
      </section>
    </div>
  );
}

function ToastView({ toast }: { toast: Toast }) {
  return (
    <div className={`toast ${toast.tone ?? 'neutral'}`}>
      <span>{toast.title}</span>
      {toast.actionLabel && toast.onAction ? (
        <button type="button" className="toastAction" onClick={toast.onAction}>
          {toast.actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function DashboardMetric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="dashboardMetric">
      <span>{label}</span>
      <strong>{value}</strong>
      {sub ? <em>{sub}</em> : null}
    </div>
  );
}

function DashboardBars({
  rows,
  mode,
  language
}: {
  rows: any[];
  mode: 'hourly' | 'daily';
  language: Language;
}) {
  const keys = mode === 'hourly'
    ? [
        { key: 'app_opened', className: 'opened' },
        { key: 'taps', className: 'taps' },
        { key: 'claims', className: 'claims' },
        { key: 'topups', className: 'topups' }
      ]
    : [
        { key: 'new_players', className: 'opened' },
        { key: 'taps', className: 'taps' },
        { key: 'claims', className: 'claims' },
        { key: 'topups', className: 'topups' }
      ];
  const max = Math.max(1, ...rows.map((row) => keys.reduce((sum, item) => sum + Number(row[item.key] ?? 0), 0)));

  return (
    <div className="dashboardBars">
      {rows.map((row, index) => {
        const total = keys.reduce((sum, item) => sum + Number(row[item.key] ?? 0), 0);
        const label = mode === 'hourly' ? shortHour(row.bucket, language) : shortDate(row.day, language);
        return (
          <div className="dashboardBarColumn" key={`${mode}-${index}`}>
            <div className="dashboardBarTrack" title={`${label}: ${formatPoints(total, language)}`}>
              {keys.map((item) => {
                const value = Number(row[item.key] ?? 0);
                return (
                  <span
                    className={`dashboardBarSegment ${item.className}`}
                    key={item.key}
                    style={{ height: `${Math.max(0, (value / max) * 100)}%` }}
                  />
                );
              })}
            </div>
            <span>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function DashboardFunnel({ dashboard, language }: { dashboard: any; language: Language }) {
  const rows = dashboard?.funnel ?? [];
  const max = Math.max(1, ...rows.map((row: any) => Number(row.players ?? 0)));

  return (
    <section className="dashboardPanel">
      <div className="dashboardPanelHeader">
        <h2>{dashboardLabel(language, 'funnel7d')}</h2>
      </div>
      <div className="dashboardFunnel">
        {rows.map((row: any) => {
          const players = Number(row.players ?? 0);
          return (
            <div className="funnelRow" key={row.label}>
              <span>{formatAnalyticsEvent(row.label, language)}</span>
              <div className="funnelTrack">
                <i style={{ width: `${(players / max) * 100}%` }} />
              </div>
              <strong>{formatPoints(players, language)}</strong>
              <em>{formatPoints(row.events, language)} {dashboardLabel(language, 'events')}</em>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DashboardList({ title, rows, language }: { title: string; rows: any[]; language: Language }) {
  return (
    <section className="dashboardPanel">
      <div className="dashboardPanelHeader">
        <h2>{title}</h2>
      </div>
      <div className="dashboardList">
        {rows.length ? rows.map((row, index) => (
          <div className="dashboardListRow" key={`${title}-${index}`}>
            <span>{row.label ?? row.display_name ?? row.status ?? row.ip_hash_prefix ?? '-'}</span>
            <strong>{formatPoints(row.players ?? row.taps ?? row.taps_count ?? row.player_count ?? row.spent_points ?? 0, language)}</strong>
          </div>
        )) : (
          <div className="dashboardEmpty">{dashboardLabel(language, 'noActiveRound')}</div>
        )}
      </div>
    </section>
  );
}

function AdminDashboard({ dashboard, language, tt }: { dashboard: any; language: Language; tt: T }) {
  if (!dashboard) return null;
  const live = dashboard.live ?? {};
  const players = dashboard.players ?? {};
  const economy = dashboard.economy ?? {};
  const currentRound = dashboard.currentRound;
  const risk = dashboard.risk ?? {};

  return (
    <section className="adminDashboard">
      <div className="dashboardTitleRow">
        <div>
          <div className="kicker">{dashboardLabel(language, 'dashboard')}</div>
          <h2>{dashboardLabel(language, 'liveNow')}</h2>
        </div>
        <span>{dashboardLabel(language, 'generatedAt')}: {formatDateTime(dashboard.generatedAt, language)}</span>
      </div>

      <div className="dashboardMetricGrid">
        <DashboardMetric label={dashboardLabel(language, 'activePlayers15m')} value={formatPoints(live.activePlayers15m, language)} sub={`${formatPoints(live.activeSessions15m, language)} ${dashboardLabel(language, 'activeSessions15m')}`} />
        <DashboardMetric label={dashboardLabel(language, 'taps15m')} value={formatPoints(live.taps15m, language)} sub={`${formatPoints(live.taps24h, language)} ${dashboardLabel(language, 'taps24h')}`} />
        <DashboardMetric label={dashboardLabel(language, 'claims24h')} value={formatPoints(live.claims24h, language)} sub={`${formatPoints(live.claims15m, language)} / 15m`} />
        <DashboardMetric label={dashboardLabel(language, 'spent24h')} value={formatPoints(live.spentPoints24h, language)} sub={`${formatPoints(live.platformFee24h, language)} ${dashboardLabel(language, 'platformFee')}`} />
        <DashboardMetric label={dashboardLabel(language, 'newPlayers24h')} value={formatPoints(players.newNamed24h, language)} sub={`${formatPoints(players.incomplete, language)} ${tt('incompleteEntries')}`} />
        <DashboardMetric label={dashboardLabel(language, 'topups24h')} value={formatPoints(live.topupPoints24h, language)} sub={`${formatPoints(live.topups24h, language)} ${dashboardLabel(language, 'events')}`} />
      </div>

      <div className="dashboardGrid">
        <section className="dashboardPanel dashboardRoundPanel">
          <div className="dashboardPanelHeader">
            <h2>{dashboardLabel(language, 'currentRound')}</h2>
            {currentRound ? <span>{tt('round')} #{currentRound.sequence}</span> : null}
          </div>
          {currentRound ? (
            <div className="roundDashboard">
              <DashboardMetric label={tt('bank')} value={formatPoints(currentRound.bank_points, language)} />
              <DashboardMetric label={tt('participants')} value={formatPoints(currentRound.participants_count, language)} />
              <DashboardMetric label={tt('taps')} value={formatPoints(currentRound.taps_count, language)} />
              <DashboardMetric label={dashboardLabel(language, 'found')} value={formatPoints(currentRound.found_count, language)} sub={`${formatPoints(currentRound.hidden_count, language)} ${dashboardLabel(language, 'hidden')}`} />
              <DashboardMetric label={tt('nextBankPreview')} value={formatPoints(currentRound.next_bank_preview, language)} />
              <DashboardMetric label={dashboardLabel(language, 'endsAt')} value={formatDateTime(currentRound.ends_at, language)} />
            </div>
          ) : (
            <div className="dashboardEmpty">{dashboardLabel(language, 'noActiveRound')}</div>
          )}
        </section>

        <section className="dashboardPanel">
          <div className="dashboardPanelHeader">
            <h2>{dashboardLabel(language, 'economy7d')}</h2>
          </div>
          <div className="economyGrid">
            <DashboardMetric label={dashboardLabel(language, 'totalSpent')} value={formatPoints(economy.totalSpentPoints, language)} />
            <DashboardMetric label={dashboardLabel(language, 'spent24h')} value={formatPoints(live.spentPoints24h, language)} />
            <DashboardMetric label={dashboardLabel(language, 'platformFee')} value={formatPoints(economy.platformFee7d, language)} />
            <DashboardMetric label={dashboardLabel(language, 'nextBankContribution')} value={formatPoints(economy.nextBankContribution7d, language)} />
          </div>
        </section>
      </div>

      <div className="dashboardGrid wide">
        <section className="dashboardPanel">
          <div className="dashboardPanelHeader">
            <h2>{dashboardLabel(language, 'hourlyDynamics')}</h2>
            <span>{tt('tap')} / {dashboardLabel(language, 'claims24h')} / {dashboardLabel(language, 'topups24h')}</span>
          </div>
          <DashboardBars rows={dashboard.hourly ?? []} mode="hourly" language={language} />
        </section>

        <section className="dashboardPanel">
          <div className="dashboardPanelHeader">
            <h2>{dashboardLabel(language, 'dailyDynamics')}</h2>
            <span>{dashboardLabel(language, 'newPlayers24h')} / {tt('taps')}</span>
          </div>
          <DashboardBars rows={dashboard.daily ?? []} mode="daily" language={language} />
        </section>
      </div>

      <div className="dashboardGrid thirds">
        <DashboardFunnel dashboard={dashboard} language={language} />
        <section className="dashboardPanel">
          <div className="dashboardPanelHeader">
            <h2>{dashboardLabel(language, 'topPlayers')}</h2>
          </div>
          <div className="dashboardList">
            {(dashboard.topPlayers ?? []).map((player: any) => (
              <div className="dashboardListRow" key={player.id}>
                <span>{player.display_name}</span>
                <strong>{formatPoints(player.taps, language)}</strong>
                <em>{formatPoints(player.reward_points, language)} {dashboardLabel(language, 'rewards')}</em>
              </div>
            ))}
          </div>
        </section>
        <section className="dashboardPanel">
          <div className="dashboardPanelHeader">
            <h2>{dashboardLabel(language, 'riskSignals')}</h2>
          </div>
          <div className="riskGrid">
            <DashboardMetric label={dashboardLabel(language, 'sharedIpGroups')} value={formatPoints(risk.sharedIpGroups, language)} />
            <DashboardMetric label={dashboardLabel(language, 'sharedIpPlayers')} value={formatPoints(risk.sharedIpPlayers, language)} />
            <DashboardMetric label={dashboardLabel(language, 'rejectedTaps24h')} value={formatPoints(risk.rejectedTaps24h, language)} />
            <DashboardMetric label={dashboardLabel(language, 'blockedPlayers')} value={formatPoints(risk.blockedPlayers, language)} />
          </div>
        </section>
      </div>

      <div className="dashboardGrid thirds">
        <DashboardList title={dashboardLabel(language, 'platforms')} rows={dashboard.devices?.platforms ?? []} language={language} />
        <DashboardList title={dashboardLabel(language, 'languages')} rows={dashboard.devices?.languages ?? []} language={language} />
        <DashboardList title={dashboardLabel(language, 'timezones')} rows={dashboard.devices?.timezones ?? []} language={language} />
      </div>

      <section className="dashboardPanel">
        <div className="dashboardPanelHeader">
          <h2>{dashboardLabel(language, 'latestRounds')}</h2>
        </div>
        <div className="roundsDashboardTable">
          {(dashboard.latestRounds ?? []).map((round: any) => (
            <div className="roundsDashboardRow" key={round.id}>
              <strong>{tt('round')} #{round.sequence}</strong>
              <span>{round.status}</span>
              <span>{tt('bank')}: {formatPoints(round.bank_points, language)}</span>
              <span>{tt('participants')}: {formatPoints(round.participants_count, language)}</span>
              <span>{tt('taps')}: {formatPoints(round.taps_count, language)}</span>
              <span>{dashboardLabel(language, 'found')}: {formatPoints(round.found_count, language)}</span>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}

function AdminConsole({ language, tt, onToast }: { language: Language; tt: T; onToast: (toast: Toast) => void }) {
  const [overview, setOverview] = useState<any>(null);
  const [dashboard, setDashboard] = useState<any>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [nextOverview, nextDashboard, nextPlayers, nextAnalytics] = await Promise.all([
        api.adminOverview(),
        api.adminDashboard(),
        api.adminPlayers(),
        api.adminAnalytics()
      ]);
      setOverview(nextOverview);
      setDashboard(nextDashboard);
      setPlayers(nextPlayers.players);
      setAnalytics(nextAnalytics);
    } catch (error) {
      onToast({ title: errorTitle(error, language, 'adminRequestFailed'), tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  const mutate = async (fn: () => Promise<any>, title: string) => {
    setBusy(true);
    try {
      await fn();
      onToast({ title, tone: 'good' });
      await load();
    } catch (error) {
      onToast({ title: errorTitle(error, language, 'adminActionFailed'), tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const activeRoundId = overview?.currentState?.state === 'active' ? overview.currentState.round.id : null;

  return (
    <main className="adminPage">
      <div className="adminHeader">
        <div>
          <div className="kicker">{tt('admin')}</div>
          <h1>{tt('operations')}</h1>
        </div>
        <a href="/">{tt('game')}</a>
      </div>

      <div className="adminAuth">
        <button type="button" onClick={load} disabled={busy}>
          <RefreshCw size={18} />
          {tt('load')}
        </button>
      </div>

      {overview ? (
        <>
          <AdminDashboard dashboard={dashboard} language={language} tt={tt} />

          <section className="adminCards">
            <div className="adminCard"><span>{tt('players')}</span><strong>{formatPoints(overview.totals.namedPlayers ?? overview.totals.players, language)}</strong></div>
            <div className="adminCard muted"><span>{tt('incompleteEntries')}</span><strong>{formatPoints(overview.totals.incompletePlayers, language)}</strong></div>
            <div className="adminCard"><span>{tt('taps')}</span><strong>{formatPoints(overview.totals.taps, language)}</strong></div>
            <div className="adminCard"><span>{tt('spent')}</span><strong>{formatPoints(overview.totals.spentPoints, language)}</strong></div>
            <div className="adminCard"><span>{tt('pois')}</span><strong>{formatPoints(overview.pois.total, language)}</strong></div>
          </section>

          <section className="adminActions">
            <button type="button" onClick={() => mutate(() => api.adminGeneratePois(), tt('poiPoolGenerated'))} disabled={busy}>
              <MapPin size={18} />
              {tt('generatePois')}
            </button>
            <button type="button" onClick={() => mutate(() => api.adminCreateRound(), tt('roundCreated'))} disabled={busy}>
              <Play size={18} />
              {tt('createRound')}
            </button>
            <button type="button" onClick={() => activeRoundId && mutate(() => api.adminFinalizeRound(activeRoundId), tt('roundFinalized'))} disabled={!activeRoundId || busy}>
              <Ban size={18} />
              {tt('finalizeRound')}
            </button>
            {Object.entries(overview.settings ?? {}).map(([setting, value]) => (
              <label className="toggle" key={setting}>
                <input
                  type="checkbox"
                  checked={Boolean(value)}
                  onChange={(event) => mutate(
                    () => api.adminSetSetting(setting, event.target.checked),
                    `${formatAdminSetting(setting, language)} ${tt('settingUpdated')}`
                  )}
                />
                <span>{formatAdminSetting(setting, language)}</span>
              </label>
            ))}
          </section>

          <section className="adminColumns">
            <div className="adminTable">
              <h2>{tt('players')}</h2>
              <p className="adminTableHint">{tt('playersListHint')}</p>
              {players.map((player) => (
                <div className="adminPlayer" key={player.id}>
                  <div>
                    <strong>{player.display_name ?? tt('noName')}</strong>
                    <span>{player.id}</span>
                    <dl className="adminTechDetails">
                      <div><dt>{tt('sessions')}</dt><dd>{formatPoints(player.session_count, language)}</dd></div>
                      <div><dt>{tt('lastSeen')}</dt><dd>{player.last_seen_at ? formatDateTime(player.last_seen_at, language) : tt('notAvailable')}</dd></div>
                      <div><dt>{tt('ipAddress')}</dt><dd>{compactValue(player.latest_ip_address, tt('notAvailable'))}</dd></div>
                      <div><dt>{tt('sameIpPlayers')}</dt><dd>{formatPoints(player.same_ip_player_count, language)}</dd></div>
                      <div><dt>{tt('browser')}</dt><dd>{compactValue(player.user_agent, tt('notAvailable'))}</dd></div>
                      <div><dt>{tt('device')}</dt><dd>{compactValue(player.platform, tt('notAvailable'))}</dd></div>
                      <div><dt>{tt('screen')}</dt><dd>{formatScreen(player, tt('notAvailable'))}</dd></div>
                      <div><dt>{tt('timezone')}</dt><dd>{compactValue(player.browser_timezone, tt('notAvailable'))}</dd></div>
                      <div><dt>{tt('language')}</dt><dd>{compactValue(player.browser_languages ?? player.browser_language ?? player.accept_language, tt('notAvailable'))}</dd></div>
                      <div><dt>{tt('source')}</dt><dd>{compactValue(player.referrer, tt('notAvailable'))}</dd></div>
                    </dl>
                  </div>
                  <em>{formatPoints(player.points_balance, language)}</em>
                  <button type="button" onClick={() => mutate(() => api.adminGrant(player.id, 200), tt('pointsGranted'))}>+200</button>
                  <button type="button" onClick={() => mutate(() => api.adminBlock(player.id, !player.is_blocked), player.is_blocked ? tt('playerUnblocked') : tt('playerBlocked'))}>
                    {player.is_blocked ? tt('unblock') : tt('block')}
                  </button>
                </div>
              ))}
            </div>
            <div className="adminTable">
              <h2>{tt('analytics')}</h2>
              {(analytics?.funnel ?? []).slice(0, 14).map((row: any) => (
                <div className="analyticsRow" key={row.event_name}>
                  <span>{formatAnalyticsEvent(row.event_name, language)}</span>
                  <strong>{formatPoints(row.count, language)}</strong>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </main>
  );
}

export function App() {
  const [language, setLanguage] = useState<Language>(() => readStoredLanguage());
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [player, setPlayer] = useState<Player | null>(null);
  const [roundState, setRoundState] = useState<RoundState | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpBusy, setTopUpBusy] = useState(false);
  const [treasurePopup, setTreasurePopup] = useState<FoundTreasure | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileData, setProfileData] = useState<PlayerProfile | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const isAdmin = useMemo(() => window.location.pathname.startsWith('/admin'), []);
  const isRules = useMemo(() => window.location.pathname.startsWith('/rules'), []);
  const tt = useMemo<T>(() => (key) => t(language, key), [language]);

  const setAndStoreLanguage = (nextLanguage: Language) => {
    setLanguage(nextLanguage);
    window.localStorage.setItem('hotcold_language', nextLanguage);
  };

  useEffect(() => {
    const meta = languageMeta(language);
    document.documentElement.lang = meta.code;
    document.documentElement.dir = meta.dir;
  }, [language]);

  useEffect(() => {
    document.body.classList.toggle('adminMode', isAdmin);
    return () => document.body.classList.remove('adminMode');
  }, [isAdmin]);

  useEffect(() => {
    document.body.classList.toggle('pageMode', isRules);
    return () => document.body.classList.remove('pageMode');
  }, [isRules]);

  const showToast = (next: Toast) => {
    setToast(next);
    window.setTimeout(() => setToast(null), next.onAction ? 7000 : 3200);
  };

  const submitTopUp = async (amount: number) => {
    setTopUpBusy(true);
    try {
      const result = await api.demoTopUp(amount);
      setPlayer(result.player);
      setTopUpOpen(false);
      showToast({ title: `${tt('topUpSuccess')}: +${formatPoints(amount, language)}`, tone: 'good' });
    } catch (error) {
      showToast({ title: errorTitle(error, language, 'topUpFailed'), tone: 'bad' });
    } finally {
      setTopUpBusy(false);
    }
  };

  const refreshRound = async () => {
    try {
      const next = await api.currentRound();
      setRoundState(next);
    } catch (error) {
      showToast({ title: errorTitle(error, language, 'refreshFailed'), tone: 'bad' });
    }
  };

  const openProfile = async (playerId: string) => {
    setProfileOpen(true);
    setProfileBusy(true);
    setProfileData(null);
    try {
      const result = await api.playerProfile(playerId);
      setProfileData(result.profile);
    } catch (error) {
      showToast({ title: errorTitle(error, language, 'loadFailed'), tone: 'bad' });
    } finally {
      setProfileBusy(false);
    }
  };

  useEffect(() => {
    if (isAdmin || isRules) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [nextConfig, session] = await Promise.all([api.config(), api.session()]);
        if (cancelled) return;
        setConfig(nextConfig);
        setPlayer(session.player);
        setRoundState(session.roundState);
        void api.sessionTelemetry(browserTelemetryPayload()).catch(() => undefined);
      } catch (error) {
        showToast({ title: errorTitle(error, language, 'loadFailed'), tone: 'bad' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, isRules, language]);

  useEffect(() => {
    if (isAdmin || isRules) return;
    const timer = window.setInterval(() => void refreshRound(), 10_000);
    return () => window.clearInterval(timer);
  }, [isAdmin, isRules, language]);

  if (isAdmin) {
    return (
      <>
        <LanguageSwitcher language={language} className="adminLanguageSwitcher" onLanguage={setAndStoreLanguage} />
        <AdminConsole language={language} tt={tt} onToast={showToast} />
        {toast ? <ToastView toast={toast} /> : null}
      </>
    );
  }

  if (isRules) {
    return (
      <>
        <LanguageSwitcher language={language} className="rulesLanguageSwitcher" onLanguage={setAndStoreLanguage} />
        <RulesPage language={language} />
      </>
    );
  }

  if (loading || !config || !player) {
    return (
      <div className="loadingScreen">
        <LanguageSwitcher language={language} className="loadingLanguageSwitcher" onLanguage={setAndStoreLanguage} />
        <Compass size={34} />
        <span>{tt('loadingHotCold')}</span>
      </div>
    );
  }

  return (
    <main className="appShell">
      {!player.onboardingComplete ? (
        <Onboarding player={player} config={config} language={language} tt={tt} onLanguage={setAndStoreLanguage} onPlayer={setPlayer} onToast={showToast} />
      ) : null}
      <GameMap
        config={config}
        roundState={roundState}
        player={player}
        language={language}
        tt={tt}
        onToast={showToast}
        onInsufficientFunds={() => {
          setToast(null);
          setTopUpOpen(true);
        }}
        onTapResult={(response) => {
          setPlayer(response.player);
          setRoundState(response.roundState);
          if (response.tap.claimResult === 'success' && response.tap.treasureFound) {
            setTreasurePopup({
              rewardPoints: response.tap.treasureFound.rewardPoints,
              treasureCategory: response.tap.treasureFound.category,
              place: response.tap.treasureFound.place,
              foundAt: new Date().toISOString()
            });
          }
        }}
      />
      <RoundPanel
        roundState={roundState}
        player={player}
        config={config}
        language={language}
        tt={tt}
        onLanguage={setAndStoreLanguage}
        onProfile={() => void openProfile(player.id)}
        onRefresh={() => void refreshRound()}
      />
      <ActivityPanel
        roundState={roundState}
        language={language}
        tt={tt}
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        onToggle={() => setActivityOpen((current) => !current)}
        onPlayerProfile={(playerId) => void openProfile(playerId)}
      />
      {topUpOpen ? (
        <TopUpModal
          language={language}
          tt={tt}
          busy={topUpBusy}
          onSubmit={submitTopUp}
          onClose={() => setTopUpOpen(false)}
        />
      ) : null}
      {treasurePopup ? (
        <TreasureFoundModal
          treasure={treasurePopup}
          language={language}
          tt={tt}
          onClose={() => setTreasurePopup(null)}
        />
      ) : null}
      {profileOpen ? (
        <ProfileModal
          profile={profileData}
          busy={profileBusy}
          language={language}
          tt={tt}
          onClose={() => setProfileOpen(false)}
        />
      ) : null}
      {toast ? <ToastView toast={toast} /> : null}
    </main>
  );
}
